import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  type AnimalStatusKind,
  animalStatusOptionsQueryOptions,
  type KeyedStringMapAttribute,
  keyedStringMapQueryOptions,
} from '@/services/animal-management/api';
import { ORIGIN_STATE_CODES } from '@/services/animal-management/origin';
import { useAccessToken } from '@/services/auth';

export type SelectOption = { value: string; label: string };

type OptionSelectProps = {
  id: string;
  options: SelectOption[];
  value: string | null | undefined;
  onChange: (value: string) => void;
  /** Label for a stored value that is not in the options. */
  selectedLabel?: string;
  invalid?: boolean;
  disabled?: boolean;
};

export function OptionSelect({
  id,
  options,
  value,
  onChange,
  selectedLabel,
  invalid,
  disabled,
}: OptionSelectProps) {
  const { t } = useTranslation();
  // Keep a stored value selectable even if it is no longer an option, e.g.
  // legacy free-text origins or inactive statuses.
  const items = useMemo(
    () =>
      value && !options.some((o) => o.value === value)
        ? [{ value, label: selectedLabel || value }, ...options]
        : options,
    [options, value, selectedLabel],
  );

  return (
    <Select
      items={items}
      value={value ?? ''}
      onChange={(next) => onChange((next as string | null) ?? '')}
      disabled={disabled}
    >
      <SelectTrigger id={id} aria-invalid={invalid} className="w-full">
        <SelectValue placeholder={t('common.selectOption', 'Select Option')} />
      </SelectTrigger>
      <SelectContent>
        {items.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export const ORIGIN_OPTIONS: SelectOption[] = ORIGIN_STATE_CODES.map(
  (code) => ({
    value: code,
    label: code,
  }),
);

export const YES_NO_OPTIONS: SelectOption[] = [
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No' },
];

export function useKeyedStringMapOptions(
  objectTypeCode: number,
  attribute: KeyedStringMapAttribute,
  enabled = true,
) {
  const token = useAccessToken();
  const {
    data = [],
    isLoading,
    isError,
  } = useQuery({
    ...keyedStringMapQueryOptions(token, objectTypeCode, attribute),
    enabled: !!token && enabled,
  });
  return {
    options: data,
    isLoading,
    unavailable: !isLoading && (isError || data.length === 0),
  };
}

export function useAnimalStatusOptions(kind: AnimalStatusKind, enabled = true) {
  const token = useAccessToken();
  const {
    data = [],
    isLoading,
    isError,
  } = useQuery({
    ...animalStatusOptionsQueryOptions(token, kind),
    enabled: !!token && enabled,
  });
  const options = useMemo<SelectOption[]>(
    () =>
      data.filter((o) => o.name).map((o) => ({ value: o.id, label: o.name! })),
    [data],
  );
  return {
    options,
    isLoading,
    unavailable: !isLoading && (isError || options.length === 0),
  };
}
